import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Loader2, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import { CONSULT_OPTIONS } from "@/lib/catalog";
import { useAuth } from "@/context/AuthContext";
import { track } from "@/lib/analytics";

const field = "w-full border-b border-white/15 bg-transparent py-3 text-white placeholder:text-slate-400 outline-none transition-colors focus:border-[#00F0FF]";
const chevron = { backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E\")" };
const selectCls = `${field} cursor-pointer appearance-none bg-[length:16px] bg-[right_0_center] bg-no-repeat pr-6`;
const BUDGETS = ["Under ₹10,000", "₹10,000 to ₹30,000", "₹30,000 to ₹60,000", "Above ₹60,000"];
const EMPTY = { name: "", business: "", phone: "", email: "", service: "", call_time: "", message: "", website: "", budget: "" };

const Success = ({ onReset }) => (
  <div className="rounded-3xl border border-[#00F0FF]/40 bg-[#0E1526] p-8 sm:p-10" data-testid="consult-success">
    <CheckCircle2 className="h-10 w-10 text-[#00F0FF]" />
    <h3 className="mt-5 font-display text-2xl font-bold text-white sm:text-3xl">Thank you! Your free consultation request is received.</h3>
    <p className="mt-3 text-slate-300">We'll contact you on your mobile number to confirm a time. The initial consultation is free of charge.</p>
    <button onClick={onReset} className="mt-6 rounded-full border border-white/20 px-5 py-2.5 text-sm text-white hover:border-[#00F0FF]" data-testid="consult-new-request">Send another request</button>
  </div>
);

export const ConsultForm = ({ preset, defaultService = "", source = "home" }) => {
  const { user } = useAuth();
  const [form, setForm] = useState({ ...EMPTY, service: defaultService });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (preset?.service) { setDone(false); setForm((f) => ({ ...f, service: preset.service })); }
  }, [preset]);
  useEffect(() => {
    if (user) setForm((f) => ({ ...f, name: f.name || user.name, email: f.email || user.email }));
  }, [user]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!/^[6-9]\d{9}$/.test(form.phone.replace(/\D/g, "").slice(-10))) { setError("Please enter a valid 10-digit Indian mobile number."); return; }
    setSending(true);
    try {
      const { service, ...rest } = form;
      await api.post("/contact", { ...rest, services: service ? [service] : [] });
      track("generate_lead", { form: "free_consultation", service, source });
      setDone(true);
      setForm({ ...EMPTY, service: defaultService });
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(Array.isArray(d) ? "Please check the highlighted fields and try again." : d || "Sorry, your request could not be sent. Please try again or call us.");
    } finally {
      setSending(false);
    }
  };

  if (done) return <Success onReset={() => setDone(false)} />;

  return (
    <form onSubmit={submit} className="rounded-3xl border border-white/10 bg-[#0E1526] p-6 sm:p-10" data-testid="contact-form" noValidate={false}>
      <p className="font-display text-xl font-bold text-white sm:text-2xl">Book your free consultation</p>
      <p className="mt-1 text-sm text-slate-300">No charges for the initial consultation. Fields marked * are required.</p>
      <div className="mt-6 grid gap-6 sm:grid-cols-2">
        <input required minLength={2} autoComplete="name" aria-label="Customer name" placeholder="Customer name *" value={form.name} onChange={set("name")} className={field} data-testid="contact-name-input" />
        <input autoComplete="organization" aria-label="Business name" placeholder="Business name" value={form.business} onChange={set("business")} className={field} data-testid="contact-business-input" />
        <input required type="tel" inputMode="numeric" autoComplete="tel" aria-label="Mobile number" placeholder="Mobile number *" value={form.phone} onChange={set("phone")} className={field} data-testid="contact-phone-input" />
        <input required type="email" autoComplete="email" aria-label="Email address" placeholder="Email address *" value={form.email} onChange={set("email")} className={field} data-testid="contact-email-input" />
        <select required aria-label="Service required" value={form.service} onChange={set("service")} style={chevron} className={`${selectCls} ${form.service ? "" : "text-slate-400"}`} data-testid="contact-service-select">
          <option value="" className="bg-[#0E1526]">Service required *</option>
          {CONSULT_OPTIONS.map((o) => <option key={o} value={o} className="bg-[#0E1526] text-white">{o}</option>)}
        </select>
        <input aria-label="Preferred consultation time" placeholder="Preferred consultation time" value={form.call_time} onChange={set("call_time")} className={field} data-testid="contact-calltime-input" />
        <input aria-label="Current website" placeholder="Current website (optional)" value={form.website} onChange={set("website")} className={field} data-testid="contact-website-input" />
        <select aria-label="Estimated budget" value={form.budget} onChange={set("budget")} style={chevron} className={`${selectCls} ${form.budget ? "" : "text-slate-400"}`} data-testid="contact-budget-select">
          <option value="" className="bg-[#0E1526]">Estimated budget (optional)</option>
          {BUDGETS.map((b) => <option key={b} value={b} className="bg-[#0E1526] text-white">{b}</option>)}
        </select>
      </div>
      <textarea rows={3} aria-label="Brief description of requirements" placeholder="Brief description of your requirements" value={form.message} onChange={set("message")} className={`${field} mt-6 resize-none`} data-testid="contact-message-input" />
      {error && <p className="mt-4 rounded-xl border border-red-400/40 bg-red-500/10 px-4 py-3 text-sm text-red-200" role="alert" data-testid="contact-error">{error}</p>}
      <p className="mt-6 text-xs text-slate-400">Your details are used only to respond to your enquiry. Read our <Link to="/privacy-policy" className="text-[#00F0FF] underline" data-testid="contact-privacy-link">Privacy Policy</Link>.</p>
      <button type="submit" disabled={sending} data-testid="contact-form-submit-button"
        className="group mt-6 flex items-center gap-2 rounded-full bg-[#00F0FF] px-7 py-4 font-semibold text-[#070B14] transition-transform hover:scale-[1.03] disabled:opacity-60">
        {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowUpRight className="h-4 w-4 transition-transform group-hover:rotate-45" />}
        {sending ? "Sending…" : "Book Free Consultation"}
      </button>
    </form>
  );
};
